import { create } from 'zustand';

const BREAKPOINTS = {
  small: 800,
  medium: 1279,
  large: 1280,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

type BreakpointState = {
  breakpoint: Breakpoint;
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
};

function getCurrentBreakpoint(): Breakpoint {
  const width = window.innerWidth;
  for (const [key, value] of Object.entries(BREAKPOINTS)) {
    if (width < value) {
      return key as Breakpoint;
    }
  }
  return 'large';
}

export const useBreakpointStore = create<BreakpointState>(() => {
  // Set up initial state
  const breakpoint = getCurrentBreakpoint();

  // Set up listeners
  const mqls = Object.entries(BREAKPOINTS).map(([key, value]) => ({
    key: key as Breakpoint,
    mql: window.matchMedia(`(max-width: ${value - 1}px)`),
  }));

  const onChange = () => {
    const breakpoint = getCurrentBreakpoint();
    useBreakpointStore.setState({
      breakpoint,
      isSmall: breakpoint === 'small',
      isMedium: breakpoint === 'medium',
      isLarge: breakpoint === 'large',
    });
  };

  mqls.forEach(({ mql }) => mql.addEventListener('change', onChange));

  // Return initial state
  return {
    breakpoint,
    isSmall: breakpoint === 'small',
    isMedium: breakpoint === 'medium',
    isLarge: breakpoint === 'large',
  };
});

// Convenience hooks
export const useIsSmallScreen = () => useBreakpointStore((state) => state.isSmall);
export const useIsMediumScreen = () => useBreakpointStore((state) => state.isMedium);
export const useIsLargeScreen = () => useBreakpointStore((state) => state.isLarge);
export const useBreakpoint = () => useBreakpointStore((state) => state.breakpoint);
